import { buildProperty } from "@firecms/core";

import { SUBMIT_STATE_LABELS } from "./apple_delegated_onboarding.types";

/**
 * THE OWNERSHIP BOUNDARY, DECLARED ONCE FOR EVERY SURFACE THAT CARRIES A STEP STATUS — the five
 * embedded steps (brand, media, location, locationCta, brandAssets) plus the `showcase_requests` and
 * `gallery_photos` subcollections. All of them carry the identical status shape, so they share these
 * properties rather than keeping near-copies that could drift apart on the one thing that must not
 * drift.
 *
 * ⚠️ RETRACTED: this said "ALL FIVE SURFACES — the four embedded steps and the showcase
 * subcollection". The count was already wrong when written (there are five embedded steps, not four)
 * and the gallery subcollection has since joined. A COUNT IS NOT WORTH THE MAINTENANCE — it is the
 * kind of detail that silently goes stale every time a surface is added, so what matters is named
 * instead: every surface whose status fields follow this shape uses these properties, and a new one
 * must too.
 *
 * ⚠️ The two subcollections keep their status fields at the DOCUMENT ROOT (`submitState`, not
 * `<step>.submitState`) because there is one record per document, which is why the shared helpers take
 * a step name of `null` for them.
 *
 * ⚠️ EVERY BACKEND-OWNED FIELD IS `readOnly: true`, NOT MERELY HIDDEN. A hidden field is still part
 * of the form's values and can still be written by a save; a read-only one renders through
 * `ReadOnlyFieldBinding`, which has no way to call `setValue`. Read-only is the half that stops a
 * human EDITING these; `stripFieldsTheFormMayNotWrite` in the collection's `onPreSave` is the half
 * that stops the save echoing their loaded values back over a fresher backend write.
 *
 * ⚠️ `submitState` IS READ-ONLY HERE TOO, even though the CMS is allowed to write it. It is written
 * exclusively by the gated queue / re-open buttons, through the one helper in
 * `apple_delegated_onboarding.write.ts`. An editable dropdown would be a one-click way past every
 * gate in this console — and past the `DRAFT`/`QUEUED` restriction the contract puts on us.
 *
 * @param resourceNoun what this step's Apple write creates, for the `companyId` warning copy.
 */
export const stepStatusProperties = ({ resourceNoun }: { resourceNoun: string }) => ({
  /**
   * ⚠️ CMS-WRITABLE, AND IT MUST NORMALLY BE LEFT EMPTY. The backend resolves the client's Apple
   * company from their OAuth grant; a value typed here overrides that resolution, which is precisely
   * how a write lands in the WRONG Apple company. Its one legitimate use is clearing a
   * `GRANT_CONFLICT` for a client whose DearDoc account resolves to two ACTIVE Apple companies —
   * a benign situation Apple itself creates when a brand gets a second location.
   */
  companyId: buildProperty({
    name: "Pinned Apple Company ID (leave empty)",
    description: `⚠️ Leave this empty. The backend resolves the client's Apple company from their grant, and a value typed here overrides that — which is how a ${resourceNoun} gets created in the wrong client's Apple company. Fill it in only to clear a GRANT_CONFLICT failure, and only with a company id an engineer has confirmed — clearing this field afterwards genuinely removes it, so the backend goes back to resolving the company from the grant. The company id shown in the Apple result below is the RESOLVED one and is not typed by anyone.`,
    dataType: "string",
  }),

  submitState: buildProperty({
    name: "Status",
    description:
      "Written by the Queue and Re-open buttons on the Queue tab, never by this form — those buttons are where the safety checks live.",
    readOnly: true,
    dataType: "string",
    enumValues: SUBMIT_STATE_LABELS,
  }),

  queuedAt: buildProperty({
    name: "Queued At",
    description: "Set by the Queue button. Audit only — the drain does not order by it.",
    readOnly: true,
    hideFromCollection: true,
    dataType: "date",
  }),

  writeError: buildProperty({
    name: "Could Not Queue",
    description:
      "Set by this console when the queue write itself failed — “I could not even enqueue”. The backend never reads it, and it is cleared the next time the step is queued successfully.",
    readOnly: true,
    hideFromCollection: true,
    dataType: "string",
  }),

  /* ---------------------------------- BACKEND-OWNED below --------------------------------------
   * Everything from here down is written ONLY by the drain. Read-only in the form and stripped from
   * every form save; see the file header.
   * -------------------------------------------------------------------------------------------- */

  claimedAt: buildProperty({
    name: "Claimed At (backend)",
    description:
      "When the drain claimed this step transactionally. The stuck-step timeout is measured from here.",
    readOnly: true,
    hideFromCollection: true,
    dataType: "date",
  }),

  attempts: buildProperty({
    name: "Attempts (backend)",
    description:
      "⚠️ Apple attempts, incremented inside the claim transaction and capped at 3. Failures that PROVE nothing reached Apple reset it, so a non-zero count means attempts whose outcome we could not confirm. Only the backend may change it — resetting it by hand is how a duplicate live Apple resource gets minted.",
    readOnly: true,
    hideFromCollection: true,
    dataType: "number",
  }),

  completedAt: buildProperty({
    name: "Completed At (backend)",
    readOnly: true,
    hideFromCollection: true,
    dataType: "date",
  }),

  failureCode: buildProperty({
    name: "Failure Code (backend)",
    description:
      "Machine-readable reason. The set grows without a CMS release, so this console never maps it to copy of its own — read the message below, which is the authoritative explanation.",
    readOnly: true,
    hideFromCollection: true,
    dataType: "string",
  }),

  failureMessage: buildProperty({
    name: "Failure Reason (backend)",
    description:
      "⚠️ Written by the backend FOR STAFF and rendered verbatim. Do not clear it to “dismiss” a failure — it is the only record of what went wrong, and the drain overwrites it itself on the next claim. Use Re-open on the Queue tab instead.",
    readOnly: true,
    multiline: true,
    dataType: "string",
  }),

  /**
   * ⚠️ THE BUCKET THIS CONSOLE MUST NEVER WRITE. `<step>.apple.<ENV>` is what makes the backend's id
   * pins real: the showcase gate refuses a request whose `input.locationId` does not match
   * `location.apple.<ENV>.locationId`. A CMS-written value would defeat that gate entirely and allow
   * a real, live showcase on the wrong place card of the same client.
   *
   * Declared as a read-only key/value map with no child properties on purpose: the backend adds
   * fields to these buckets, and a hand-declared mirror would quietly stop showing the new ones.
   */
  apple: buildProperty({
    name: "Apple Result (backend)",
    description:
      "⚠️ Written only by the backend, and bucketed PER ENVIRONMENT (PRODUCTION / AIE / DQE) because Apple mints different ids in each. The status above is NOT per-environment, so a step run in one environment reads “Submitted” in all of them — this is the bucket that answers “is it live here?”. The Queue tab names the environment it is showing.",
    readOnly: true,
    hideFromCollection: true,
    dataType: "map",
    keyValue: true,
  }),
});
